import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import { Plus, Trash2 } from 'lucide-react';

export const StepCulture = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const handleChange = (field: string, value: string | string[] | number) => {
    updateFormData('culture', { [field]: value });
    clearError(field);
  };

  const handleManagementTeamChange = (value: 'yes' | 'no') => {
    updateFormData('culture', {
      managementTeam: value,
      managementTeamExplanation: value === 'yes' ? formData.culture.managementTeamExplanation : '',
    });
    clearError('managementTeam');
    clearError('managementTeamExplanation');
  };

  // ── Core values — free form ──────────────────────────────────────────────
  const coreValues: string[] = formData.culture.coreValues || [''];
  const coreValueDescriptions: string[] = formData.culture.coreValueDescriptions || [''];

  const updateCoreValue = (index: number, value: string) => {
    const updated = [...coreValues];
    updated[index] = value;
    updateFormData('culture', { coreValues: updated });
  };

  const updateCoreValueDescription = (index: number, value: string) => {
    const updated = [...coreValueDescriptions];
    updated[index] = value;
    updateFormData('culture', { coreValueDescriptions: updated });
  };

  const addCoreValue = () => {
    if (coreValues.length >= 8) return;
    updateFormData('culture', {
      coreValues: [...coreValues, ''],
      coreValueDescriptions: [...coreValueDescriptions, ''],
    });
  };

  const removeCoreValue = (index: number) => {
    if (coreValues.length <= 1) return;
    updateFormData('culture', {
      coreValues: coreValues.filter((_, i) => i !== index),
      coreValueDescriptions: coreValueDescriptions.filter((_, i) => i !== index),
    });
  };

  // ── Language detection ───────────────────────────────────────────────────
  const isFr = (t as any)?.lang === 'fr' || (t?.step9 as any)?.coreValues?.includes('valeur');

  const labels = {
    sectionCoreValues: isFr ? 'Valeurs fondamentales de l\'entreprise' : 'Core Company Values',
    sectionCoreValuesDesc: isFr
      ? 'Décrivez les valeurs qui guident votre entreprise au quotidien. Nommez-les à votre façon — ce sont VOS valeurs, pas des catégories imposées.'
      : 'Describe the values that guide your business every day. Name them in your own words — these are YOUR values, not imposed categories.',
    valueName: isFr ? 'Nom de la valeur' : 'Value name',
    valueNamePlaceholder: isFr ? 'Ex: Intégrité, Service client, Innovation...' : 'E.g. Integrity, Customer Service, Innovation...',
    valueDesc: isFr ? 'Comment cette valeur se manifeste concrètement' : 'How this value is applied in practice',
    valueDescPlaceholder: isFr
      ? 'Ex: Nous sommes transparents avec nos clients même quand les nouvelles sont difficiles...'
      : 'E.g. We are transparent with clients even when delivering difficult news...',
    addValue: isFr ? 'Ajouter une valeur' : 'Add a value',
    removeValue: isFr ? 'Supprimer' : 'Remove',
    maxValues: isFr ? 'Maximum 8 valeurs' : 'Maximum 8 values',

    managementTeam: (t.step9 as any)?.managementTeam || (isFr ? 'Équipe de direction en place' : 'Management team in place'),
    managementTeamExp: (t.step9 as any)?.managementTeamExplanation || (isFr ? 'Décrivez l\'équipe' : 'Describe the team'),

    idealSuccessor: t.step9?.idealSuccessor || (isFr ? 'Successeur idéal' : 'Ideal successor'),
    idealSuccessorPlaceholder: t.step9?.idealSuccessorPlaceholder || '',
    biggestFear: t.step9?.biggestFear || (isFr ? 'Plus grande crainte concernant la vente' : 'Biggest fear regarding the sale'),
    biggestFearPlaceholder: t.step9?.biggestFearPlaceholder || '',

    transitionPeriod: t.step9?.transitionPeriod || (isFr ? 'Période de transition' : 'Transition Period'),
    postSaleConsulting: t.step9?.postSaleConsulting || (isFr ? 'Disponibilité pour consultation post-vente' : 'Post-sale consulting availability'),

    dealBreakers: t.step9?.dealBreakers || (isFr ? 'Conditions non négociables (deal breakers)' : 'Non-negotiable conditions (deal breakers)'),
    dealBreakersPlaceholder: t.step9?.dealBreakersPlaceholder || '',

    transitionDuration: t.step9?.transitionDuration || (isFr ? 'Durée de transition souhaitée' : 'Preferred transition duration'),
    nonCompetePeriod: (t.step9 as any)?.nonCompetePeriod || (isFr ? 'Période de non-concurrence' : 'Non-compete period'),
    nonCompeteRadius: isFr ? 'Rayon de non-concurrence' : 'Non-compete radius',
    nonCompeteRadiusHint: isFr
      ? "Distance géographique sur laquelle l'entente de non-concurrence s'applique."
      : 'Geographic distance over which the non-compete agreement applies.',
  };

  // Enriched post-sale consulting options
  const consultingOptions = [
    { value: 'yes_full_time', labelFr: 'Oui — temps plein (préciser durée)', labelEn: 'Yes — full time (specify duration)' },
    { value: 'yes_part_time', labelFr: 'Oui — temps partiel', labelEn: 'Yes — part time' },
    { value: 'yes_on_demand', labelFr: 'Oui — sur demande seulement', labelEn: 'Yes — on demand only' },
    { value: 'negotiable', labelFr: 'Négociable selon l\'acheteur', labelEn: 'Negotiable based on buyer' },
    { value: 'no', labelFr: 'Non disponible', labelEn: 'Not available' },
  ];

  return (
    <div className="space-y-8">

      {/* ── Section 1 — Core Values (free form) ── */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">{labels.sectionCoreValues}</Label>
          <p className="text-sm text-muted-foreground mt-1">{labels.sectionCoreValuesDesc}</p>
        </div>

        <div className="space-y-4">
          {coreValues.map((val, index) => (
            <div key={index} className="space-y-2 p-4 border rounded-lg bg-muted/30 relative">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm font-semibold text-muted-foreground">
                  {isFr ? `Valeur ${index + 1}` : `Value ${index + 1}`}
                </Label>
                {coreValues.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCoreValue(index)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <Input
                value={val || ''}
                onChange={(e) => updateCoreValue(index, e.target.value)}
                placeholder={labels.valueNamePlaceholder}
                className="font-medium"
              />
              <Textarea
                value={(coreValueDescriptions)[index] || ''}
                onChange={(e) => updateCoreValueDescription(index, e.target.value)}
                placeholder={labels.valueDescPlaceholder}
                rows={2}
                className="text-sm"
              />
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {coreValues.length < 8 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addCoreValue}
              className="gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              {labels.addValue}
            </Button>
          )}
          <span className="text-xs text-muted-foreground">{labels.maxValues}</span>
        </div>
      </div>

      {/* ── Section 2 — Management team ── */}
      <div className="space-y-4 border rounded-lg p-4">
        <Label className="text-base font-semibold">{labels.managementTeam}</Label>
        <RadioGroup
          value={formData.culture.managementTeam}
          onValueChange={(value: 'yes' | 'no') => handleManagementTeamChange(value)}
          className="flex flex-col space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="managementTeamYes" />
            <Label htmlFor="managementTeamYes" className="font-normal">
              {isFr ? 'Oui — une équipe de direction est en place' : 'Yes — a management team is in place'}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="managementTeamNo" />
            <Label htmlFor="managementTeamNo" className="font-normal">
              {isFr ? 'Non — je gère l\'entreprise seul(e)' : 'No — I manage the business myself'}
            </Label>
          </div>
        </RadioGroup>
        {formData.culture.managementTeam === 'yes' && (
          <div className="space-y-2 mt-3">
            <Label htmlFor="managementTeamExplanation">{labels.managementTeamExp}</Label>
            <Textarea
              id="managementTeamExplanation"
              value={formData.culture.managementTeamExplanation}
              onChange={(e) => handleChange('managementTeamExplanation', e.target.value)}
              placeholder={isFr
                ? 'Ex: Directeur des opérations (12 ans), contrôleur financier (5 ans), responsable RH...'
                : 'E.g. Operations Manager (12 yrs), Financial Controller (5 yrs), HR Manager...'}
              rows={3}
            />
          </div>
        )}
      </div>

      {/* ── Section 3 — Ideal successor ── */}
      <div className="space-y-2">
        <Label htmlFor="idealSuccessorDescription" className="text-base font-semibold">
          {labels.idealSuccessor}
        </Label>
        <p className="text-sm text-muted-foreground">
          {isFr
            ? 'Décrivez le profil idéal de l\'acheteur : compétences, expérience, valeurs, vision...'
            : 'Describe the ideal buyer profile: skills, experience, values, vision...'}
        </p>
        <Textarea
          id="idealSuccessorDescription"
          value={formData.culture.idealSuccessorDescription}
          onChange={(e) => handleChange('idealSuccessorDescription', e.target.value)}
          placeholder={labels.idealSuccessorPlaceholder || (isFr
            ? 'Ex: Quelqu\'un qui valorise nos employés, qui a de l\'expérience en gestion d\'équipe...'
            : 'E.g. Someone who values our employees, has team management experience...')}
          rows={4}
        />
      </div>

      {/* ── Section 4 — Biggest fear ── */}
      <div className="space-y-2">
        <Label htmlFor="biggestFearRegardingSale" className="text-base font-semibold">
          {labels.biggestFear}
        </Label>
        <p className="text-sm text-muted-foreground">
          {isFr
            ? 'Cette information aide l\'équipe AcquizGroup™ à mieux vous accompagner.'
            : 'This information helps the AcquizGroup™ team better support you through the process.'}
        </p>
        <Textarea
          id="biggestFearRegardingSale"
          value={formData.culture.biggestFearRegardingSale}
          onChange={(e) => handleChange('biggestFearRegardingSale', e.target.value)}
          placeholder={labels.biggestFearPlaceholder || (isFr
            ? 'Ex: Que mes employés perdent leur emploi, que la réputation de l\'entreprise soit compromise...'
            : 'E.g. That my employees lose their jobs, that the company\'s reputation is compromised...')}
          rows={3}
        />
      </div>

      {/* ── Section 5 — Transition period ── */}
      <div className="border-t pt-6 space-y-6">
        <h4 className="text-base font-semibold">{labels.transitionPeriod}</h4>

        <div className="space-y-4">
          {/* Post-sale consulting — enriched */}
          <div className="space-y-3">
            <Label className="font-medium">{labels.postSaleConsulting}</Label>
            <RadioGroup
              value={formData.culture.postSaleConsultingAvailability}
              onValueChange={(value) => handleChange('postSaleConsultingAvailability', value)}
              className="flex flex-col space-y-2"
            >
              {consultingOptions.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`consulting-${opt.value}`} />
                  <Label htmlFor={`consulting-${opt.value}`} className="font-normal">
                    {isFr ? opt.labelFr : opt.labelEn}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* If yes — show duration + details */}
            {['yes_full_time', 'yes_part_time', 'yes_on_demand'].includes(
              formData.culture.postSaleConsultingAvailability || ''
            ) && (
              <Textarea
                value={(formData.culture as any).postSaleConsultingDetails || ''}
                onChange={(e) => updateFormData('culture', { postSaleConsultingDetails: e.target.value })}
                placeholder={isFr
                  ? 'Précisez : durée, disponibilité, conditions, sujets couverts...'
                  : 'Specify: duration, availability, conditions, topics covered...'}
                rows={2}
                className="mt-2"
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Transition duration */}
            <div className="space-y-2">
              <Label>{labels.transitionDuration}</Label>
              <Select
                value={formData.culture.transitionDurationPreference}
                onValueChange={(value) => handleChange('transitionDurationPreference', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isFr ? 'Sélectionner...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30_days">{isFr ? '30 jours' : '30 days'}</SelectItem>
                  <SelectItem value="60_days">{isFr ? '60 jours' : '60 days'}</SelectItem>
                  <SelectItem value="90_days">{isFr ? '90 jours' : '90 days'}</SelectItem>
                  <SelectItem value="6_months">{isFr ? '6 mois' : '6 months'}</SelectItem>
                  <SelectItem value="12_months">{isFr ? '12 mois' : '12 months'}</SelectItem>
                  <SelectItem value="more_than_90_days">{isFr ? 'Plus de 90 jours — à négocier' : 'More than 90 days — to negotiate'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Non-compete */}
            <div className="space-y-2">
              <Label>{labels.nonCompetePeriod}</Label>
              <Select
                value={formData.culture.nonCompetePeriod}
                onValueChange={(value) => handleChange('nonCompetePeriod', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isFr ? 'Sélectionner...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries((t.step9 as any)?.nonCompetePeriodOptions || {
                    '1_year': isFr ? '1 an' : '1 year',
                    '2_years': isFr ? '2 ans' : '2 years',
                    '3_years': isFr ? '3 ans' : '3 years',
                    '5_years': isFr ? '5 ans' : '5 years',
                    'negotiable': isFr ? 'À négocier' : 'Negotiable',
                  }).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label as string}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* AJOUTÉ (28 août 2026) — Rayon de non-concurrence, avec
              sélecteur d'unité (km / miles) indépendant du pays du deal.
              Ex. tiré de Ridgeline_Manufacturing_TestData_USA.md :
              "36 mois, rayon 500 miles". */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="nonCompeteRadius">{labels.nonCompeteRadius}</Label>
              <p className="text-xs text-muted-foreground">{labels.nonCompeteRadiusHint}</p>
              <div className="flex gap-3">
                <Input
                  id="nonCompeteRadius"
                  type="number"
                  min={0}
                  value={formData.culture.nonCompeteRadius || ''}
                  onChange={(e) => handleChange('nonCompeteRadius', parseInt(e.target.value) || 0)}
                  placeholder={isFr ? 'Ex: 500' : 'E.g. 500'}
                  className="flex-1"
                />
                <Select
                  value={formData.culture.nonCompeteRadiusUnit}
                  onValueChange={(value) => handleChange('nonCompeteRadiusUnit', value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="km">{isFr ? 'km' : 'km'}</SelectItem>
                    <SelectItem value="miles">{isFr ? 'miles' : 'miles'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 6 — Current challenges & growth goals ──
           Ajouté pour préremplir StrategicPlanGenerator.tsx, qui les lisait
           déjà mais aucun champ du formulaire ne les collectait jusqu'ici. */}
      <div className="border-t pt-6 space-y-6">
        <h4 className="text-base font-semibold">
          {isFr ? 'Défis actuels et objectifs de croissance' : 'Current Challenges & Growth Goals'}
        </h4>

        <div className="space-y-2">
          <Label htmlFor="currentChallenges" className="text-base font-semibold">
            {isFr ? 'Défis actuels' : 'Current Challenges'}
          </Label>
          <p className="text-sm text-muted-foreground">
            {isFr
              ? 'Quels sont les principaux défis auxquels l\'entreprise fait face en ce moment ?'
              : 'What are the main challenges the business is currently facing?'}
          </p>
          <Textarea
            id="currentChallenges"
            value={formData.culture.currentChallenges}
            onChange={(e) => handleChange('currentChallenges', e.target.value)}
            placeholder={isFr
              ? 'Ex: Difficulté à recruter du personnel qualifié, dépendance à quelques gros clients...'
              : 'E.g. Difficulty recruiting skilled staff, dependency on a few large customers...'}
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="growthGoals" className="text-base font-semibold">
            {isFr ? 'Objectifs de croissance / Vision' : 'Growth Goals / Vision'}
          </Label>
          <p className="text-sm text-muted-foreground">
            {isFr
              ? 'Quelle est votre vision pour la croissance future de l\'entreprise sous un nouveau propriétaire ?'
              : 'What is your vision for the business\'s future growth under new ownership?'}
          </p>
          <Textarea
            id="growthGoals"
            value={formData.culture.growthGoals}
            onChange={(e) => handleChange('growthGoals', e.target.value)}
            placeholder={isFr
              ? 'Ex: Expansion vers de nouveaux marchés régionaux, lancement d\'une gamme de services complémentaires...'
              : 'E.g. Expansion into new regional markets, launching a complementary service line...'}
            rows={3}
          />
        </div>
      </div>

      {/* ── Section 7 — Deal breakers ── */}
      <div className="space-y-2">
        <Label htmlFor="dealBreakersFromSeller" className="text-base font-semibold">
          {labels.dealBreakers}
        </Label>
        <p className="text-sm text-muted-foreground">
          {isFr
            ? 'Conditions qui vous feraient refuser une offre, peu importe le prix.'
            : 'Conditions that would cause you to refuse an offer, regardless of price.'}
        </p>
        <Textarea
          id="dealBreakersFromSeller"
          value={formData.culture.dealBreakersFromSeller}
          onChange={(e) => handleChange('dealBreakersFromSeller', e.target.value)}
          placeholder={labels.dealBreakersPlaceholder || (isFr
            ? 'Ex: Ne pas maintenir les emplois existants, déplacer l\'entreprise hors de la région...'
            : 'E.g. Not keeping existing employees, relocating the business outside the region...')}
          rows={3}
        />
      </div>

    </div>
  );
};